import type { AddMyFilesToRequestBody } from "@nlc/shared-types";

import { useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import {
  CircleHelpIcon,
  MessageSquareMoreIcon,
  SendIcon,
  UploadIcon,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { Button as RACButton, TooltipTrigger } from "react-aria-components";
import { FormProvider } from "react-hook-form";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { useMediaPreviewModal } from "@/components/media-preview-modal/useMediaPreviewModal.tsx";
import { Modal } from "@/components/modal";
import { Switch } from "@/components/switch";
import { Tooltip } from "@/components/tooltip";
import { FeatureGuard } from "@/features/auth/permissions/feature-guard";
import { ChatTextarea } from "@/features/request/edit/components/chat-section/components/chat-input/ChatTextArea";
import { useIsMobileScreen } from "@/hooks/useIsMobileScreen";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime";

import { ConfirmDialog } from "./components/confirm-dialog";
import { DropZone } from "./components/dropzone";
import { FileItemPreview } from "./components/file-item-preview";
import { FileListDisplay } from "./components/file-list-display";
import { ALLOWED_FILE_TYPES } from "./config";
import { useFileDropZone } from "./hooks/useFileDropZone";
import { useMentionableUsers } from "./hooks/useMentionableUsers";
import { useModalInteractions } from "./hooks/useModalInteractions";
import { useSendMyFiles } from "./hooks/useSendMyFiles";
import { useUploadFiles, useUploadFilesForm } from "./hooks/useUploadFiles";

const getHeaderContent = (step: 1 | 2) =>
  step === 1
    ? {
        icon: <UploadIcon />,
        subtitle: (
          <>
            <span>Upload the design and source files for this project.</span>
            <br />
            <span>
              Update the file name, and identify if it is a source file before
              proceeding.
            </span>
          </>
        ),
        title: "Upload Files",
      }
    : {
        icon: <MessageSquareMoreIcon />,
        subtitle: (
          <>
            <span>
              Please confirm your designs and message before sending them to the
              client.
            </span>
            <br />
            <span>
              Include any specific details that you want to share based on your
              design process, and tag the client to notify them.
            </span>
          </>
        ),
        title: "Input your message for the client",
      };

export function UploadFilesModal() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { openUpload, requestId } = useSearch({
    strict: false,
  });
  const isMobile = useIsMobileScreen();

  const { setMediaItems } = useMediaPreviewModal();

  const sendMyFilesMutation = useSendMyFiles();
  const { data: mentionableUsers } = useMentionableUsers(requestId);

  const formMethods = useUploadFilesForm();
  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    trigger,
    watch,
  } = formMethods;

  const {
    addFiles,
    cancelUpload,
    deleteAllFiles,
    deleteFile,
    fields,
    isUploading,
    uploadingCount,
  } = useUploadFiles(formMethods);

  const handleDeleteFile = (id: number) => {
    deleteFile(id);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteAllFiles = () => {
    deleteAllFiles();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAddFiles = (files: FileList | null) => {
    addFiles(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const {
    currentStep,
    handleBackPress,
    handleCancelPress,
    handleDiscardConfirm,
    handleDiscardDismiss,
    handleNextPress,
    handleOpenChange,
    handleSetMessage,
    handleToggleSendToChat,
    isDiscardDialogOpen,
    isModalOpen,
    isSendToChat,
    message,
    openModal,
  } = useModalInteractions({
    hasFiles: fields.length > 0,
    isUploading: isUploading,
    onDiscardConfirmed: handleDeleteAllFiles,
    requestId,
  });

  const { icon, subtitle, title } = getHeaderContent(currentStep);

  useEffect(() => {
    if (openUpload) {
      openModal();
    }
  }, [openUpload]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fields.length > 0) {
      setTimeout(() => {
        trigger("files");
      }, 0);
    } else if (errors.files) {
      clearErrors("files");
    }
  }, [fields, trigger, clearErrors, errors.files]);

  const fileNames = watch("files");

  useEffect(() => {
    if (fields.length > 1) {
      setTimeout(() => {
        trigger("files");
      }, 0);
    }
  }, [fileNames, trigger, fields.length]);

  useEffect(() => {
    if (currentStep === 2 && fileNames.length > 0) {
      const mediaItems = fileNames.map((field) => ({
        extension: getExtensionFromFilename(field.file.name) || "",
        id: String(field.attachmentId),
        name: field.name,
        url: URL.createObjectURL(field.file),
      }));

      setMediaItems(mediaItems);
    }
  }, [currentStep, fileNames, setMediaItems]);

  const { dropZoneProps, isDragging } = useFileDropZone({
    accept: ALLOWED_FILE_TYPES.join(","),
    onFilesDropped: handleAddFiles,
  });

  const handleUploadAreaClick = () => {
    fileInputRef.current?.click();
  };

  const handleFinalSubmit = handleSubmit(async (data) => {
    if (!requestId) {
      console.error("Request ID is not defined");

      return;
    }
    try {
      const myFilesData = data.files.map((file) => ({
        attachmentId: file.attachmentId || 0,
        fileName: file.file.name,
        isSourceFile: file.isSourceFile,
        name: `${file.name}.${getExtensionFromFilename(file.originalName)}`,
      }));

      const mentionedUserIds: number[] = getMentionedIds(String(message));

      const requestBody: AddMyFilesToRequestBody = {
        mentionedUserIds:
          mentionedUserIds.length > 0 ? mentionedUserIds : undefined,
        message: message,
        myFiles: myFilesData,
        postOnActivityLog: isSendToChat,
      };

      await sendMyFilesMutation.mutateAsync({
        body: requestBody,
        requestId,
      });

      queryClient.invalidateQueries({
        queryKey: ["myFilesStructure"],
      });

      handleDiscardConfirm();
    } catch (error) {
      console.error("Error submitting files:", error);
    }
  });

  return (
    <>
      {requestId && (
        <FeatureGuard feature="my-files.files.upload">
          <Button onPress={openModal} variant="secondary">
            <UploadIcon />
            {isMobile ? "" : "Upload New File"}
          </Button>
        </FeatureGuard>
      )}
      <Modal
        classNames={{ modal: "w-[1068px] h-[656px]" }}
        isOpen={isModalOpen}
        onOpenChange={handleOpenChange}
      >
        <Dialog>
          <DialogHeader className="border-b border-b-border-secondary p-3xl">
            <div className="flex gap-xl">
              <div className="size-12 shrink-0 grid place-items-center rounded-lg border border-border-secondary">
                {icon}
              </div>
              <div className="flex flex-col gap-xs">
                <span className="text-lg font-semibold text-text-primary">
                  {title}
                </span>
                <span className="text-sm font-normal text-text-tertiary">
                  {subtitle}
                </span>
              </div>
            </div>
          </DialogHeader>
          <DialogBody className="px-3xl py-2xl gap-[20px] overflow-y-hidden">
            <FormProvider {...formMethods}>
              {currentStep === 1 && (
                <div className="flex flex-row gap-xl">
                  <input
                    accept={ALLOWED_FILE_TYPES.join(",")}
                    className="hidden"
                    multiple
                    onChange={(e) => handleAddFiles(e.target.files)}
                    ref={fileInputRef}
                    type="file"
                  />
                  <DropZone
                    dropZoneProps={dropZoneProps}
                    isDragging={isDragging}
                    onClick={handleUploadAreaClick}
                  />
                  <FileListDisplay
                    fields={fields}
                    isUploading={isUploading}
                    onCancel={cancelUpload}
                    onDelete={handleDeleteFile}
                    onDeleteAll={handleDeleteAllFiles}
                  />
                </div>
              )}
              {currentStep === 2 && (
                <div className="flex flex-row gap-xl">
                  <div className="flex flex-col gap-[12px]">
                    <span className="text-md font-medium text-text-primary">
                      List of uploaded files
                    </span>
                    <div className="grid grid-cols-4 gap-[8px] h-min w-[362px] max-h-[338px] overflow-y-auto">
                      {fileNames.map((field) => (
                        <FileItemPreview field={field} key={field.id} />
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-[12px] flex-grow">
                    <span className="text-md font-medium text-text-primary">
                      Your message
                    </span>
                    <ChatTextarea
                      className="min-h-[320px] max-h-[320px]"
                      disabled={false}
                      mentionSuggestions={mentionableUsers}
                      onChange={(e) => {
                        handleSetMessage({
                          mentionedUserIds: getMentionedIds(e),
                          message: e,
                        });
                      }}
                      showBubbleMenu={false}
                      showMenuBar={true}
                      value={String(message)}
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-center">
                <div className="flex items-center gap-lg">
                  <div
                    className={`size-[8px] rounded-full ${currentStep === 1 ? "bg-fg-brand-primary-alt" : "bg-bg-quaternary"}`}
                  ></div>
                  <div
                    className={`size-[8px] rounded-full ${currentStep === 2 ? "bg-fg-brand-primary-alt" : "bg-bg-quaternary"}`}
                  ></div>
                </div>
              </div>
            </FormProvider>
          </DialogBody>
          <DialogFooter className="border-t border-t-border-secondary p-3xl">
            <div className="flex gap-[16px] items-center pr-[8px]">
              <Switch
                isSelected={isSendToChat}
                onChange={handleToggleSendToChat}
              >
                Send to chat
              </Switch>
              <TooltipTrigger delay={0}>
                <RACButton>
                  <CircleHelpIcon className="text-fg-quinary" size={16} />
                </RACButton>
                <Tooltip>
                  {isSendToChat ? (
                    <div className="flex flex-col gap-xs">
                      <span>Enable Send to Chat</span>
                      <span className="font-medium">
                        The files will be sent to the chat and the customer will
                        be able to view and add Feedback Tool comments to
                        request for revisions.
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-xs">
                      <span>Disable Send to Chat</span>
                      <span className="font-medium">
                        The files will only be uploaded in My Files. The client
                        will not be able to see them in the chat, or use the
                        Feedback Tool to request for revisions.
                      </span>
                    </div>
                  )}
                </Tooltip>
              </TooltipTrigger>
            </div>
            {currentStep === 1 && (
              <div className="flex gap-lg items-center">
                {errors.files && (
                  <span className="text-sm text-regular text-text-error-primary">
                    {errors.files.root?.message}
                  </span>
                )}
                <Button
                  color="gray"
                  onPress={handleCancelPress}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  isDisabled={
                    isUploading || fields.length === 0 || !!errors.files
                  }
                  onPress={handleNextPress}
                >
                  {isUploading
                    ? `Uploading ${uploadingCount} file(s)...`
                    : "Next"}
                </Button>
              </div>
            )}
            {currentStep === 2 && (
              <>
                <Button
                  color="gray"
                  onPress={handleBackPress}
                  variant="secondary"
                >
                  Back
                </Button>
                <Button
                  isDisabled={sendMyFilesMutation.isPending}
                  onPress={() => handleFinalSubmit()}
                >
                  <SendIcon />
                  {sendMyFilesMutation.isPending
                    ? "Submitting..."
                    : "Upload & Submit Designs"}
                </Button>
              </>
            )}
          </DialogFooter>
        </Dialog>
      </Modal>

      <ConfirmDialog
        isOpen={isDiscardDialogOpen}
        onConfirm={handleDiscardConfirm}
        onDismiss={handleDiscardDismiss}
        variant="discard"
      />
    </>
  );
}

function getMentionedIds(commentHtml: string): number[] {
  const matches = commentHtml.match(/data-id="(\d+)"/g) || [];

  // eslint-disable-next-line unicorn/prefer-string-replace-all
  return matches.map((match) => Number(match.replace(/\D/g, "")));
}
